/**
 * Screen Editor per-field guide-key resolver.
 *
 * Many editor fields are built by SHARED FieldConfig literals (e.g. the media
 * appearance fields in v2FlexibleMediaConfig, or createV2ImageStyleFields /
 * createV2AppearanceStyleFields helpers) that are reused across several sections.
 * A shared literal cannot carry a section-specific `guideKey`, so this resolver
 * injects the correct key at RENDER TIME from the single source of truth
 * (SCREEN_EDITOR_FIELD_GUIDE_DEFINITIONS), keyed by (sectionType, FieldConfig.path).
 *
 * Scope: only catalog entries whose `fieldKey` IS the real FieldConfig `path`
 * resolve here — i.e. the per-field sections (media, flexibleSections, headerBar,
 * textField, copyBlock, roller, paywallHeader/Body/Footer, ...). Sections whose
 * catalog fieldKeys are synthetic accordion-group labels (actionBar's
 * `primary-gradient`, paywallMedia's `scale-mode`, options/carousel's
 * `titleStyles`, ...) document a whole group and are wired at their group header
 * instead; they simply do not match by path here and the resolver returns
 * `undefined` for them — no regression, just no auto-injection.
 *
 * See modules/support-flow/MODULE.md (per-field guide derivation) and the catalog
 * at src/modules/reactFlow/config/v2ScreenEditorFieldGuides.ts.
 */
import type { V2SectionType } from '@modules/reactFlow/config/v2ScreenTypeConfig';
import { SCREEN_EDITOR_FIELD_GUIDE_DEFINITIONS } from '@modules/reactFlow/config/v2ScreenEditorFieldGuides';
import type { FieldConfig } from '@modules/reactFlow/types/editorFieldTypes';

/** `${sectionType}::${fieldPath}` → guideKey. Paths and section types never contain "::". */
const GUIDE_KEY_BY_SECTION_FIELD: ReadonlyMap<string, string> = new Map(
  SCREEN_EDITOR_FIELD_GUIDE_DEFINITIONS.map(def => [`${def.sectionType}::${def.fieldKey}`, def.guideKey]),
);

/**
 * Resolve the per-field guide key for a (sectionType, FieldConfig.path) pair, or
 * `undefined` when the catalog does not document that path as a real field in that
 * section (synthetic group keys, undocumented fields).
 */
export function resolveScreenEditorFieldGuideKey(
  sectionType: V2SectionType,
  fieldPath: string,
): string | undefined {
  return GUIDE_KEY_BY_SECTION_FIELD.get(`${sectionType}::${fieldPath}`);
}

/**
 * Returns `config` enriched with the catalog `guideKey` for `sectionType` when the
 * config does not already declare one. Pure: never mutates the shared input, and
 * returns the SAME reference when nothing is added so React identity is preserved
 * for already-keyed / non-documented fields.
 */
export function withScreenEditorFieldGuide<T extends FieldConfig>(
  config: T,
  sectionType: V2SectionType,
): T {
  if (config.guideKey) return config;
  const guideKey = resolveScreenEditorFieldGuideKey(sectionType, config.path);
  return guideKey ? { ...config, guideKey } : config;
}

/** Array convenience wrapper around {@link withScreenEditorFieldGuide}. */
export function withScreenEditorFieldGuides<T extends FieldConfig>(
  configs: readonly T[],
  sectionType: V2SectionType,
): T[] {
  return configs.map(config => withScreenEditorFieldGuide(config, sectionType));
}
